import { useContext } from "react";
import { appContext } from "../../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram, faLinkedin } from "@fortawesome/free-brands-svg-icons";

interface FooterProps {
    whyChooseUsRef: React.RefObject<HTMLDivElement>;
    priceEstimationRef: React.RefObject<HTMLDivElement>;
    contactRef: React.RefObject<HTMLDivElement>;
}

export const Footer: React.FC<FooterProps> = ({ 
    whyChooseUsRef, 
    priceEstimationRef, 
    contactRef 
}) => {
    const { darkMode } = useContext(appContext);
    const currentYear = new Date().getFullYear();

    // Function to scroll to a ref
    const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
        if (ref.current) {
            const navHeight = 80;
            window.scrollTo({ 
                top: ref.current.offsetTop - navHeight, 
                behavior: 'smooth' 
            });
        }
    };

    const footerLinks = [
        { label: "Why Choose Us", ref: whyChooseUsRef },
        { label: "Calculator", ref: priceEstimationRef },
        { label: "Contact", ref: contactRef }
    ];

    return (
        <footer className="py-16 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 transition-colors duration-500">
            <div className="container mx-auto px-4">
                <div className="flex flex-col md:flex-row justify-between items-start gap-12">
                    {/* Brand Section */}
                    <div className="w-full md:w-1/3">
                        <img
                            src={darkMode ? "/assets/segpool.png" : "/assets/segpool_blue.png"}
                            className="h-12 mb-6"
                            alt="SegPool Logo"
                        />
                        <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-sm">
                            The premier crypto mining ecosystem in the Middle East.
                            Built for reliability, speed, and transparency since 2017.
                        </p>
                    </div>

                    {/* Links & Info */}
                    <div className="grid grid-cols-2 md:grid-cols-2 gap-12 w-full md:w-auto">
                        <div>
                            <h4 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-6">
                                Quick Links
                            </h4>
                            <ul className="space-y-4 font-bold text-slate-900 dark:text-white">
                                {footerLinks.map((item, index) => (
                                    <li key={index}>
                                        <button
                                            onClick={() => scrollToRef(item.ref)}
                                            className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors text-left"
                                        >
                                            {item.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-slate-100 dark:border-slate-900 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-sm font-medium text-slate-400 dark:text-slate-500">
                        © {currentYear} Segments Cloud Computing L.L.C. All Rights Reserved.
                    </p>
                    <div className="flex items-center gap-6">
                        <a 
                            href="https://www.linkedin.com/company/segments-cloud-computing-llc" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
                            aria-label="LinkedIn"
                        >
                            <FontAwesomeIcon icon={faLinkedin} className="w-5 h-5" />
                        </a>
                        <a 
                            href="https://www.instagram.com/segments.ae" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors"
                            aria-label="Instagram"
                        >
                            <FontAwesomeIcon icon={faInstagram} className="w-5 h-5" />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};